import React, { useState, useEffect, useContext } from 'react';
import { collection, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { HashtagIcon, ChartBarIcon, ClockIcon, FireIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AuthContext from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { cleanupOldHashtags } from '../../services/hashtagCleanupService';

interface HashtagData {
  id: string;
  tag: string;
  count: number;
  score: number;
  lastUsed: Timestamp;
  postCount: number;
  createdAt?: Timestamp;
  posts?: string[];
}

const HashtagAnalytics: React.FC = () => {
  const [hashtags, setHashtags] = useState<HashtagData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cleanupStatus, setCleanupStatus] = useState<string | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const { currentUser } = useContext(AuthContext);
  useTranslation();

  // Estadísticas calculadas
  const [totalHashtags, setTotalHashtags] = useState(0);
  const [averagePostsPerHashtag, setAveragePostsPerHashtag] = useState(0);
  const [newestHashtag, setNewestHashtag] = useState<HashtagData | null>(null);
  const [mostUsedHashtag, setMostUsedHashtag] = useState<HashtagData | null>(null);

  useEffect(() => {
    const fetchHashtags = async () => {
      try {
        setLoading(true);
        const hashtagsQuery = query(
          collection(db, 'trendingHashtags'),
          orderBy('count', 'desc'),
          limit(100) // Obtener más hashtags para análisis
        );

        const snapshot = await getDocs(hashtagsQuery);
        const hashtagsData: HashtagData[] = [];

        snapshot.forEach(doc => {
          const data = doc.data() as HashtagData;
          hashtagsData.push({
            id: doc.id,
            tag: data.tag || `#${doc.id}`,
            count: data.count || 0,
            score: data.score || 0,
            lastUsed: data.lastUsed,
            postCount: data.posts?.length || 0,
            createdAt: data.createdAt
          });
        });

        setHashtags(hashtagsData);

        // Calcular estadísticas
        if (hashtagsData.length > 0) {
          // Total de hashtags
          setTotalHashtags(hashtagsData.length);

          // Media de posts por hashtag
          const totalPosts = hashtagsData.reduce((sum, tag) => sum + tag.postCount, 0);
          setAveragePostsPerHashtag(totalPosts / hashtagsData.length);

          // Hashtag más usado
          setMostUsedHashtag(hashtagsData[0]); // Ya está ordenado por count

          // Hashtag más reciente
          const hashtagsWithCreatedAt = hashtagsData.filter(tag => tag.createdAt);
          if (hashtagsWithCreatedAt.length > 0) {
            const sorted = [...hashtagsWithCreatedAt].sort((a, b) => {
              const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date();
              const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date();
              return dateB.getTime() - dateA.getTime();
            });
            setNewestHashtag(sorted[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching hashtags:', error);
        setError('Error al cargar los hashtags');
      } finally {
        setLoading(false);
      }
    };

    fetchHashtags();
  }, []);

  const handleCleanupHashtags = async () => {
    try {
      setCleanupLoading(true);
      setCleanupStatus('Limpiando hashtags antiguos...');

      const deletedCount = await cleanupOldHashtags(3, 30);

      setCleanupStatus(`Se han eliminado ${deletedCount} hashtags antiguos o poco usados.`);

      // Recargar los hashtags después de la limpieza
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error cleaning up hashtags:', error);
      setCleanupStatus('Error al limpiar hashtags antiguos.');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Verificar si el usuario es administrador (simplificado)
  const isAdmin = currentUser && ['admin@domired.com', 'livanlebron@gmail.com'].includes(currentUser.email || '');

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-md">
          No tienes permisos para acceder a esta página.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Helmet>
        <title>Análisis de Hashtags | DomiRed Admin</title>
      </Helmet>

      <div className="mb-6">
        <Link to="/admin" className="text-domired-600 dark:text-domired-400 hover:underline mb-4 inline-block">
          &larr; Volver al Panel de Administración
        </Link>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HashtagIcon className="h-6 w-6 mr-2 text-domired-600 dark:text-domired-400" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Análisis de Hashtags</h1>
          </div>

          <button
            onClick={handleCleanupHashtags}
            disabled={cleanupLoading}
            className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 disabled:opacity-50"
          >
            {cleanupLoading ? 'Limpiando...' : 'Limpiar Hashtags Antiguos'}
          </button>
        </div>

        {cleanupStatus && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-md">
            {cleanupStatus}
          </div>
        )}
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-2">
            <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
            <h3 className="text-lg font-semibold">Total de Hashtags</h3>
          </div>
          <p className="text-3xl font-bold">{totalHashtags}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-2">
            <ChartBarIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
            <h3 className="text-lg font-semibold">Media de Posts</h3>
          </div>
          <p className="text-3xl font-bold">{averagePostsPerHashtag.toFixed(1)}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">posts por hashtag</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-2">
            <FireIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
            <h3 className="text-lg font-semibold">Más Popular</h3>
          </div>
          {mostUsedHashtag ? (
            <>
              <p className="text-2xl font-bold">{mostUsedHashtag.tag}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{mostUsedHashtag.count} usos</p>
            </>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No disponible</p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-2">
            <ClockIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
            <h3 className="text-lg font-semibold">Más Reciente</h3>
          </div>
          {newestHashtag ? (
            <>
              <p className="text-2xl font-bold">{newestHashtag.tag}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {newestHashtag.createdAt?.toDate().toLocaleDateString()}
              </p>
            </>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No disponible</p>
          )}
        </div>
      </div>

      {/* Tabla de hashtags */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold">Listado de Hashtags</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-red-500 dark:text-red-400">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Hashtag
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Usos
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Posts
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Último Uso
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {hashtags.map((hashtag) => (
                  <tr key={hashtag.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-domired-600 dark:text-domired-400">
                      {hashtag.tag}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {hashtag.count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {hashtag.postCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {hashtag.lastUsed?.toDate().toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link
                        to={`/hashtag/${hashtag.id}`}
                        className="text-domired-600 dark:text-domired-400 hover:underline"
                      >
                        Ver Posts
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HashtagAnalytics;
